import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { DeviceInfo, DeviceState, MeasurementResult } from '@/types/device';
import type { MeasurementProfile } from '@/types/protobuf';

/**
 * Electrode Type interface
 * Matches backend ElectrodeTypeDto
 */
export interface ElectrodeType {
  id: string;
  name: string;
  manufacturer: string;
  channelCount: number;
  version: string;
  isActive: boolean;
}

/**
 * Patient interface - minimal for Phase 0/1
 * Will be expanded in Phase 2
 */
export interface Patient {
  id: string;
  name?: string;
  age?: number;
  // Add more fields as needed
}

/**
 * Measurement wizard state management
 * Supports 7-step measurement workflow (5 setup + 2 measurement phases)
 */
interface MeasurementWizardState {
  // Navigation
  currentStep: number;
  
  // Test ID (created after patient/sample info)
  testId: string | null;
  
  // Step 1: Device
  selectedDevice: DeviceInfo | null;
  deviceConnected: boolean;
  initialDeviceSnapshot: DeviceState | null;  // Snapshot captured at wizard startup
  
  // Step 2: Patient & Sample
  selectedPatient: Patient | null;
  sampleCode: string;
  additionalInfo: any | null;
  
  // Step 3: Electrode (NEW)
  selectedElectrodeType: ElectrodeType | null;
  electrodeConnected: boolean; // User confirms electrode is connected
  
  // Step 4: Profile
  selectedProfile: MeasurementProfile | null;
  
  // Step 5: Baseline Measurement (NEW)
  baselineResult: MeasurementResult | null;
  baselineCompletedAt: Date | null;
  baselineProgress: number; // 0-100 (overall only)
  
  // Step 6: (no specific state - uses timers)
  
  // Step 7: Specimen Measurement (NEW)
  specimenResult: MeasurementResult | null;
  specimenStartedAt: Date | null;
  specimenProgress: number; // 0-100 (overall only)
  
  // Step 8: Results (combines baseline + specimen)
  
  // Patient panel state (Task 05)
  selectedPatientId: string | null;
  patientPanelMode: 'selection' | 'details' | 'add-new' | 'edit' | 'anonymous';

  // Recovery: tracks the current measurement lifecycle phase for reload recovery.
  // 'baseline'          — baseline is actively running on the device (Step 3)
  // 'baseline-complete' — baseline finished, waiting for user to start specimen (Steps 4–5)
  // 'incubating'        — specimen started (Step 5 clicked), incubation timer running; device still idle (Step 6)
  // 'specimen'          — specimen measurement is actively running on the device (Steps 6–7)
  measurementInFlight: {
    phase: 'baseline' | 'baseline-complete' | 'incubating' | 'specimen';
    startedAt: number; // Date.now() timestamp
    deviceUid: string; // Validate we reconnect to the same device
  } | null;

  /** When true, resetWizard() skips clearing persisted state. Set during recovery. */
  recoveryInProgress: boolean;

  // Actions
  setCurrentStep: (step: number) => void;
  setTestId: (id: string | null) => void;
  setSelectedDevice: (device: DeviceInfo | null) => void;
  setDeviceConnected: (connected: boolean) => void;
  setInitialDeviceSnapshot: (snapshot: DeviceState | null) => void;
  setSelectedPatient: (patient: Patient | null) => void;
  setSampleCode: (code: string) => void;
  setAdditionalInfo: (info: any | null) => void;
  setSelectedPatientId: (patientId: string | null) => void;
  setPatientPanelMode: (mode: 'selection' | 'details' | 'add-new' | 'edit' | 'anonymous') => void;
  setSelectedElectrodeType: (type: ElectrodeType | null) => void;
  setElectrodeConnected: (connected: boolean) => void;
  setSelectedProfile: (profile: MeasurementProfile | null) => void;
  setBaselineResult: (result: MeasurementResult | null) => void;
  setBaselineCompletedAt: (date: Date | null) => void;
  setBaselineProgress: (progress: number) => void;
  setSpecimenResult: (result: MeasurementResult | null) => void;
  setSpecimenStartedAt: (date: Date | null) => void;
  setSpecimenProgress: (progress: number) => void;
  setMeasurementInFlight: (info: { phase: 'baseline' | 'baseline-complete' | 'incubating' | 'specimen'; deviceUid: string }) => void;
  clearMeasurementInFlight: () => void;
  setRecoveryInProgress: (active: boolean) => void;
  resetWizard: () => void;
  canProceedToStep: (step: number) => boolean;
}

/**
 * Initial state for the wizard
 */
const initialState = {
  currentStep: 1,
  testId: null,
  selectedDevice: null,
  deviceConnected: false,
  initialDeviceSnapshot: null,
  selectedPatient: null,
  sampleCode: '',
  additionalInfo: null,
  selectedElectrodeType: null,
  electrodeConnected: false,
  selectedProfile: null,
  baselineResult: null,
  baselineCompletedAt: null,
  baselineProgress: 0,
  specimenResult: null,
  specimenStartedAt: null,
  specimenProgress: 0,
  selectedPatientId: null,
  patientPanelMode: 'selection' as const,
  measurementInFlight: null as null | { phase: 'baseline' | 'baseline-complete' | 'incubating' | 'specimen'; startedAt: number; deviceUid: string },
  recoveryInProgress: false,
};

/**
 * Measurement wizard store
 * Manages state for the 7-step measurement workflow (5 setup + 2 measurement phases)
 */
export const useMeasurementWizardStore = create<MeasurementWizardState>()(
  persist(
    (set, get) => ({
      // Initial state
      ...initialState,

      // Setters
      setCurrentStep: (step) => set({ currentStep: step }),
      setTestId: (id) => set({ testId: id }),
      setSelectedDevice: (device) => set({ selectedDevice: device }),
      setDeviceConnected: (connected) => set({ deviceConnected: connected }),
      setInitialDeviceSnapshot: (snapshot) => set({ initialDeviceSnapshot: snapshot }),
      setSelectedPatient: (patient) => set({ selectedPatient: patient }),
      setSampleCode: (code) => set({ sampleCode: code }),
      setAdditionalInfo: (info) => set({ additionalInfo: info }),
      setSelectedElectrodeType: (type) => set({ selectedElectrodeType: type }),
      setElectrodeConnected: (connected) => set({ electrodeConnected: connected }),
      setSelectedProfile: (profile) => set({ selectedProfile: profile }),
      setBaselineResult: (result) => set({ baselineResult: result }),
      setBaselineCompletedAt: (date) => set({ baselineCompletedAt: date }),
      setBaselineProgress: (progress) => set({ baselineProgress: progress }),
      setSpecimenResult: (result) => set({ specimenResult: result }),
      setSpecimenStartedAt: (date) => set({ specimenStartedAt: date }),
      setSpecimenProgress: (progress) => set({ specimenProgress: progress }),
      setSelectedPatientId: (patientId) => set({ selectedPatientId: patientId }),
      setPatientPanelMode: (mode) => set({ patientPanelMode: mode }),

      setMeasurementInFlight: (info) =>
        set({
          measurementInFlight: {
            phase: info.phase,
            startedAt: Date.now(),
            deviceUid: info.deviceUid,
          },
        }),

      clearMeasurementInFlight: () => set({ measurementInFlight: null }),

      setRecoveryInProgress: (active) => set({ recoveryInProgress: active }),

      // Reset wizard to initial state
      resetWizard: () => {
        set(initialState);
        localStorage.removeItem('ediphor-measurement-wizard');
      },

      // Validation: Can user proceed to target step?
      canProceedToStep: (targetStep: number) => {
        const state = get();

        switch (targetStep) {
          case 1:
            return true; // Always can go to step 1
          case 2:
            return state.deviceConnected;
          case 3:
            // Requires: device connected, test created, profile loaded
            // sampleCode not checked (auto-generated in Step 2)
            return state.deviceConnected && !!state.testId && !!state.selectedProfile;
          case 4:
            return !!state.baselineCompletedAt; // Baseline must be complete
          case 5:
            return !!state.baselineCompletedAt; // Can proceed after baseline
          case 6:
            return !!state.testId && !!state.baselineCompletedAt && !!state.specimenStartedAt;
          case 7:
            return !!state.testId && !!state.specimenStartedAt;
          default:
            return false;
        }
      },
    }),
    {
      name: 'ediphor-measurement-wizard',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      partialize: (state) => ({
        testId: state.testId,
        currentStep: state.currentStep,
        selectedProfile: state.selectedProfile,
        selectedElectrodeType: state.selectedElectrodeType,
        selectedPatientId: state.selectedPatientId,
        initialDeviceSnapshot: state.initialDeviceSnapshot,
        baselineResult: state.baselineResult,
        baselineCompletedAt: state.baselineCompletedAt,
        baselineProgress: state.baselineProgress,
        specimenStartedAt: state.specimenStartedAt,
        specimenProgress: state.specimenProgress,
        sampleCode: state.sampleCode,
        measurementInFlight: state.measurementInFlight,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.baselineCompletedAt && typeof state.baselineCompletedAt === 'string') {
          state.baselineCompletedAt = new Date(state.baselineCompletedAt as unknown as string);
        }
      },
    }
  )
);

/**
 * Selectors for performance optimization
 */

export const selectCurrentStep = (state: MeasurementWizardState) => state.currentStep;
export const selectSelectedDevice = (state: MeasurementWizardState) => state.selectedDevice;
export const selectDeviceConnected = (state: MeasurementWizardState) => state.deviceConnected;
export const selectSampleCode = (state: MeasurementWizardState) => state.sampleCode;
export const selectSelectedElectrodeType = (state: MeasurementWizardState) => state.selectedElectrodeType;
export const selectSelectedProfile = (state: MeasurementWizardState) => state.selectedProfile;
export const selectBaselineResult = (state: MeasurementWizardState) => state.baselineResult;
export const selectSpecimenResult = (state: MeasurementWizardState) => state.specimenResult;

/**
 * Selector hooks for convenience
 */

export const useCurrentWizardStep = () => useMeasurementWizardStore(selectCurrentStep);
export const useWizardDeviceConnected = () => useMeasurementWizardStore(selectDeviceConnected);
